import Container from '@/components/Container';
import getStKilianPageServerSideProps from '@/lib/serverSideProps/serverSidePropsPages/getStKilianPageServerSideProps';
import ImagesStKilianXSpiritoryLogo from '@/public/images/st-kilian/st-kilian-x-spiritory-logo.png';
import ImagesStKilianCtaImage from '@/public/images/st-kilian/st-kilian-cta-image.jpg';
import ImagesStKilianLogoOrange from '@/public/images/st-kilian/st-kilian-logo-orange.png';
import ImagesStKilianSpiritoryLogo from '@/public/images/st-kilian/st-kilian-spiritory-logo.svg';
import Image from '@/components/common/SpiritoryImage';
import StepCard from '@/components/st-kilian/StepCard';
import SalesFormModal from '@/components/st-kilian/SalesFormModal';
import { useMemo, useState, useContext } from 'react';
import ResponsiveComponent from '@/components/common/ResponsiveComponent';
import useWindowWidth from '@/hooks/useWindowWidth';
import { getCookie } from 'cookies-next';
import cookiesConstants from '@/constants/cookies.constants';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { StKilianEnum } from '@/types/translations/enums';
import { createSortOptions } from '@/components/skus/SellerPage/SellerLandingPage';
import SkusSearchLayout from '@/components/skus/SkusSearchLayout';
import { useTranslation } from 'react-i18next';
import useBackendUser from '@/hooks/useBackendUser';
import { WishlistContext } from '@/context/wishlistContext';
import SkusMobile from '@/components/skus/SkusMobile/SkusMobile';
import { Configure } from 'react-instantsearch';
import SkusDesktop from '@/components/skus/SkusDesktop';
import { FilterConfig } from '@/types/props/components/skus';
import { filterByExact } from '@/constants/search.constants';

export const getServerSideProps = getStKilianPageServerSideProps;

// Verified against the live index: the brand value has spaces, not hyphens.
const ST_KILIAN_FILTER = filterByExact('brand.name', 'St. Kilian Distillers');

export default function StKilian(): JSX.Element {
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const { translate: translateStKilian } = useTypedTranslation<StKilianEnum>('st_kilian');
  const windowWidth = useWindowWidth();

  // Maximum number of bottles to display
  const maxBottles = 9;

  // Create sort options
  const allSortsCustomTranslated = useMemo(() => createSortOptions(translate), [translate]);

  // Check if filters should be open on mobile
  const hasFilterCookie = getCookie(cookiesConstants.COOKIES.FILTERS_ARE_OPEN.name) === 'true';

  const [isSalesFormModalOpen, setIsSalesFormModalOpen] = useState(false);

  const openSalesFormModal = () => setIsSalesFormModalOpen(true);
  const closeSalesFormModal = () => setIsSalesFormModalOpen(false);

  const { t } = useTranslation('index');
  const { t: tCommon } = useTranslation('common');

  const { data: user = null } = useBackendUser();
  const wishlistContext = useContext(WishlistContext);

  const searchLayoutProps = useMemo(
    () => ({
      tCommon,
      t,
      metaTitle: {
        key: 'St. Kilian',
        value: 'St. Kilian',
        metaKey: 'St. Kilian',
      },
      brandsContent: null,
    }),
    [tCommon, t]
  );

  const filterConfig: FilterConfig = useMemo(
    () => ({
      status: true,
      category: false,
      brand: false,
      country: false,
      region: false,
      year: false,
    }),
    []
  );

  const desktopProps = useMemo(
    () => ({
      allSortsCustomTranslated,
      tCommon,
      wishlistContext,
      user,
      windowWidth,
      h1Title: 'h1Title',
      brandsContent: null,
      disableFilterRedirects: true,
      filterConfig,
    }),
    [allSortsCustomTranslated, tCommon, wishlistContext, user, windowWidth, filterConfig]
  );

  // Memoize mobile component props
  const mobileProps = useMemo(
    () => ({
      allSortsCustomTranslated,
      hasFilterCookie,
      filterConfig,
    }),
    [allSortsCustomTranslated, hasFilterCookie, filterConfig]
  );

  // Memoize the main content to avoid recreation
  const mainContent = useMemo(
    () => (
      <>
        {/* `:=` is exact in Typesense; a bare `:` is a substring match and would also pull in a
            differently-suffixed St. Kilian brand. This must be a `Configure` prop — a filter set
            anywhere else in the InstantSearch ui state never reaches the query. */}
        <Configure hitsPerPage={maxBottles} filters={ST_KILIAN_FILTER} />
        <ResponsiveComponent
          windowWidth={windowWidth}
          DesktopComponent={<SkusDesktop {...desktopProps} includeCategoryNav={false} />}
          MobileComponent={<SkusMobile {...mobileProps} />}
        />
      </>
    ),
    [windowWidth, desktopProps, mobileProps]
  );

  return (
    <>
      <div className="tw-w-full tw-bg-white tw-font-inter tw-text-white tw-antialiased">
        <div className="tw-relative tw-w-full tw-bg-black">
          <div className="tw-absolute tw-inset-0 tw-bg-stKilianBgHero tw-bg-cover tw-bg-center tw-opacity-50"></div>
          <div className="tw-relative tw-bg-opacity-50 tw-py-40 max-nine:tw-py-20">
            <Container>
              {/* section 1 */}
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
                <div className="tw-w-full tw-text-center max-sm:tw-w-full">
                  {/* add  st-kilian-x-spiritory-logo.png */}
                  <Image
                    src={ImagesStKilianXSpiritoryLogo}
                    alt={translateStKilian(StKilianEnum['alt_texts.logo'])}
                    width={455}
                    height={61}
                    className="tw-mb-12 tw-h-auto tw-max-w-full"
                  />

                  <div className="tw-w-fit">
                    <h1 className="tw-text-6xl tw-font-bold tw-uppercase max-nine:tw-text-3xl">
                      {translateStKilian(StKilianEnum['hero.title'])}
                    </h1>
                  </div>
                  <div className="tw-mt-4">
                    <p className="tw-font-plusJakartaSans tw-text-xl tw-font-medium max-nine:!tw-text-base">
                      {translateStKilian(StKilianEnum['hero.subtitle'])}
                    </p>
                  </div>
                  <div className="tw-mt-16">
                    <button
                      onClick={openSalesFormModal}
                      className="tw-cursor-pointer tw-border-none tw-bg-stKilian tw-px-6 tw-py-4 tw-font-bold tw-uppercase tw-text-text tw-no-underline"
                    >
                      {translateStKilian(StKilianEnum['hero.cta_button'])}
                    </button>
                  </div>
                </div>
              </div>
            </Container>
          </div>
        </div>
        {/* section 2 */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-stKilianBgSection tw-bg-cover tw-bg-center tw-pb-9 tw-pt-16 tw-text-center tw-text-text">
          <Container>
            <div className="nine:tw-px-40">
              <h2 className="tw-text-4xl tw-font-bold tw-uppercase max-nine:tw-text-3xl">
                {translateStKilian(StKilianEnum['section2.title'])}
              </h2>
            </div>
            <div className="tw-mt-4 nine:tw-px-40">
              <p>{translateStKilian(StKilianEnum['section2.description'])}</p>
            </div>
          </Container>
        </div>
        {/* section 3 */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white tw-py-12 tw-text-text">
          <div>
            <SkusSearchLayout {...searchLayoutProps}>{mainContent}</SkusSearchLayout>
          </div>
        </div>

        {/* section 4 */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-stKilian tw-py-12 tw-text-text">
          <h2 className="tw-text-center tw-text-4xl tw-font-bold tw-uppercase">
            {translateStKilian(StKilianEnum['section4.title'])}
          </h2>
          <div className="tw-mt-4">
            <p>{translateStKilian(StKilianEnum['section4.description'])}</p>
          </div>
          <div className="tw-mt-16 max-nine:tw-mt-8">
            <button
              onClick={openSalesFormModal}
              className="tw-cursor-pointer tw-border-none tw-bg-black tw-px-6 tw-py-4 tw-font-bold tw-uppercase tw-text-white tw-no-underline"
            >
              {translateStKilian(StKilianEnum['section4.cta_button'])}
            </button>
          </div>
        </div>

        {/* section 5 */}
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-white tw-pb-4 tw-pt-24 tw-text-text">
          <Container className="tw-flex tw-flex-col tw-items-center tw-justify-center">
            <h2 className="tw-text-center tw-text-5xl tw-font-bold tw-uppercase max-nine:tw-text-3xl">
              {translateStKilian(StKilianEnum['section5.title'])}
            </h2>

            <div className="tw-mt-10 tw-grid tw-grid-cols-1 tw-gap-x-4 tw-gap-y-8 tw-px-4 md:tw-grid-cols-2 lg:tw-grid-cols-4">
              <StepCard
                stepNumber={1}
                title={translateStKilian(StKilianEnum['section5.steps.step1.title'])}
                description={translateStKilian(StKilianEnum['section5.steps.step1.description'])}
                icon={
                  <svg
                    width="54"
                    height="71"
                    viewBox="0 0 54 71"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M53.0508 17.5009L36.6507 1.10067C36.2617 0.711568 35.7302 0.5 35.2009 0.5H6.49984C3.1086 0.5 0.349609 3.25899 0.349609 6.65023V64.3257C0.349609 67.7169 3.1086 70.4759 6.49984 70.4759H47.5013C50.8926 70.4759 53.6516 67.7169 53.6516 64.3257V18.9507C53.6516 18.3881 53.4133 17.8632 53.0508 17.5009ZM37.251 7.49937L46.6522 16.9006H39.301C38.1706 16.9006 37.251 15.9809 37.251 14.8505V7.49937ZM47.5013 66.3758H6.49984C5.36942 66.3758 4.44976 65.4561 4.44976 64.3257V6.65023C4.44976 5.51981 5.36942 4.60015 6.49984 4.60015H33.1508V14.8505C33.1508 18.2418 35.9098 21.0008 39.301 21.0008H49.5514V64.3257C49.5514 65.4561 48.6317 66.3758 47.5013 66.3758Z"
                      fill="#E27900"
                    />
                    <path
                      d="M39.3008 33.5742H18.8001C17.6679 33.5742 16.75 34.4921 16.75 35.6243C16.75 36.7565 17.6679 37.6744 18.8001 37.6744H39.3008C40.433 37.6744 41.3509 36.7565 41.3509 35.6243C41.3509 34.4921 40.433 33.5742 39.3008 33.5742Z"
                      fill="#E27900"
                    />
                    <path
                      d="M39.3008 41.7734H18.8001C17.6679 41.7734 16.75 42.6913 16.75 43.8235C16.75 44.9557 17.6679 45.8736 18.8001 45.8736H39.3008C40.433 45.8736 41.3509 44.9557 41.3509 43.8235C41.3509 42.6913 40.433 41.7734 39.3008 41.7734Z"
                      fill="#E27900"
                    />
                    <path
                      d="M39.3008 49.9727H18.8001C17.6679 49.9727 16.75 50.8905 16.75 52.0227C16.75 53.1549 17.6679 54.0728 18.8001 54.0728H39.3008C40.433 54.0728 41.3509 53.1549 41.3509 52.0227C41.3509 50.8905 40.433 49.9727 39.3008 49.9727Z"
                      fill="#E27900"
                    />
                    <path
                      d="M10.5999 37.6744C11.7321 37.6744 12.65 36.7565 12.65 35.6243C12.65 34.4921 11.7321 33.5742 10.5999 33.5742C9.46765 33.5742 8.5498 34.4921 8.5498 35.6243C8.5498 36.7565 9.46765 37.6744 10.5999 37.6744Z"
                      fill="#E27900"
                    />
                    <path
                      d="M10.5999 45.8736C11.7321 45.8736 12.65 44.9557 12.65 43.8235C12.65 42.6913 11.7321 41.7734 10.5999 41.7734C9.46765 41.7734 8.5498 42.6913 8.5498 43.8235C8.5498 44.9557 9.46765 45.8736 10.5999 45.8736Z"
                      fill="#E27900"
                    />
                    <path
                      d="M10.5999 54.0728C11.7321 54.0728 12.65 53.155 12.65 52.0227C12.65 50.8905 11.7321 49.9727 10.5999 49.9727C9.46765 49.9727 8.5498 50.8905 8.5498 52.0227C8.5498 53.155 9.46765 54.0728 10.5999 54.0728Z"
                      fill="#E27900"
                    />
                  </svg>
                }
              />

              <StepCard
                stepNumber={2}
                title={translateStKilian(StKilianEnum['section5.steps.step2.title'])}
                description={translateStKilian(StKilianEnum['section5.steps.step2.description'])}
                icon={
                  <svg
                    width="68"
                    height="63"
                    viewBox="0 0 68 63"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.0045 35.6878C10.6897 35.6878 8.80591 33.8047 8.80591 31.4892C8.80591 29.1737 10.6897 27.2907 13.0045 27.2907C15.3193 27.2907 17.203 29.1737 17.203 31.4892C17.203 33.8047 15.3186 35.6878 13.0045 35.6878ZM19.3023 14.695C16.9875 14.695 15.1037 16.578 15.1037 18.8935C15.1037 21.209 16.9875 23.0921 19.3023 23.0921C21.6171 23.0921 23.5008 21.209 23.5008 18.8935C23.5008 16.578 21.6164 14.695 19.3023 14.695ZM19.3023 39.8863C16.9875 39.8863 15.1037 41.7694 15.1037 44.0849C15.1037 46.4004 16.9875 48.2834 19.3023 48.2834C21.6171 48.2834 23.5008 46.4004 23.5008 44.0849C23.5008 41.7694 21.6164 39.8863 19.3023 39.8863ZM67.5857 46.1841C67.5857 48.4996 65.7019 50.3827 63.3871 50.3827C61.0954 50.3827 57.2404 48.4675 53.1853 45.4872C51.7543 45.9455 50.2491 46.1841 48.6922 46.1841H42.0788C40.6128 46.1841 39.2783 46.9294 38.5093 48.1792C37.7402 49.4247 37.6731 50.9481 38.3287 52.2524C38.9235 53.4448 39.1818 54.5435 39.1818 55.6932C39.1818 59.7105 35.9139 62.9784 31.898 62.9784C22.9599 62.9784 14.411 59.1584 8.44274 52.4974C2.47729 45.8399 -0.377028 36.8822 0.609632 27.9218C2.20858 13.4585 13.8673 1.80123 28.3285 0.200881C47.4046 -1.91449 63.3871 12.9596 63.3871 31.4892C63.3871 33.0469 63.1478 34.5499 62.6902 35.981C65.6697 40.0368 67.5857 43.8924 67.5857 46.1841ZM48.8741 41.9681C44.7791 38.2657 40.9948 33.7522 38.8907 29.2605C33.6858 28.6237 29.3886 24.5679 28.6322 19.2742L27.7197 12.8931C27.6273 12.2388 27.8471 11.579 28.3145 11.1115C28.7798 10.6441 29.432 10.4216 30.0961 10.5181L36.4779 11.4292C41.7716 12.185 45.8267 16.4836 46.4627 21.6884C50.9552 23.7933 55.4686 27.5769 59.1704 31.6711C59.1711 31.6096 59.1879 31.5508 59.1879 31.4892C59.1879 16.4409 46.9449 4.1986 31.8973 4.1986C30.8847 4.1986 29.8414 4.25808 28.7917 4.37354C16.266 5.75907 6.16712 15.8559 4.783 28.3823C3.91389 36.2707 6.32456 43.8406 11.5686 49.6955C16.7412 55.4692 24.1502 58.7798 31.898 58.7798C33.8048 58.7798 34.9832 57.1774 34.9832 55.6932C34.9832 55.1998 34.8747 54.7317 34.6592 54.3013C33.2632 51.5212 33.3961 48.4703 34.9342 45.9749C36.4758 43.4768 39.1447 41.9856 42.0781 41.9856H48.6915C48.7531 41.9856 48.8125 41.9688 48.8741 41.9681ZM42.3202 22.2965C41.9542 18.8984 39.3273 16.077 35.8831 15.5844L32.273 15.0687L32.7895 18.6801C33.28 22.125 36.1007 24.7519 39.4994 25.1172L42.3202 22.2965ZM63.3871 46.1841C63.3871 43.3333 54.6254 30.2408 44.9632 25.5923L42.7939 27.7602C47.4431 37.4232 60.537 46.1841 63.3871 46.1841Z"
                      fill="#E27900"
                    />
                  </svg>
                }
              />
              <StepCard
                stepNumber={3}
                title={translateStKilian(StKilianEnum['section5.steps.step3.title'])}
                description={
                  <>
                    {translateStKilian(StKilianEnum['section5.steps.step3.description_part1'])}{' '}
                    <a
                      href="/auth/sign-up"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-stKilian tw-underline hover:tw-text-stKilian/80"
                    >
                      {translateStKilian(StKilianEnum['section5.steps.step3.register_link'])}
                    </a>{' '}
                    {translateStKilian(StKilianEnum['section5.steps.step3.description_part2'])}{' '}
                    <a
                      href="/dashboard/settings/seller-profile"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-text-stKilian tw-underline hover:tw-text-stKilian/80"
                    >
                      {translateStKilian(StKilianEnum['section5.steps.step3.seller_link'])}
                    </a>
                    {translateStKilian(StKilianEnum['section5.steps.step3.description_part3'])}
                  </>
                }
                icon={
                  <svg
                    width="66"
                    height="63"
                    viewBox="0 0 66 63"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.2911 19.5838C14.2911 10.5931 21.5795 3.30469 30.5701 3.30469C39.5608 3.30469 46.8494 10.5931 46.8494 19.5838C46.8494 24.5495 44.6259 28.9958 41.1204 31.9817C52.258 36.2549 60.2128 47.0767 60.2128 59.675C60.2128 61.2853 58.9074 62.5907 57.2971 62.5907C55.6868 62.5907 54.3815 61.2853 54.3815 59.675C54.3815 46.5857 43.6597 35.864 30.5704 35.864C17.4808 35.864 6.75906 46.5857 6.75906 59.675C6.75906 61.2853 5.45366 62.5907 3.8434 62.5907C2.23313 62.5907 0.927734 61.2853 0.927734 59.675C0.927734 47.0767 8.88257 36.2549 20.02 31.9817C16.5145 28.9958 14.2911 24.5495 14.2911 19.5838ZM30.5701 9.13601C24.8001 9.13601 20.1224 13.8137 20.1224 19.5838C20.1224 25.354 24.8001 30.0317 30.5701 30.0317C36.3405 30.0317 41.0181 25.354 41.0181 19.5838C41.0181 13.8137 36.3405 9.13601 30.5701 9.13601Z"
                      fill="#E27900"
                    />
                    <path
                      d="M50.4777 6.21875C48.8762 6.21875 47.5781 7.52415 47.5781 9.13441C47.5781 10.7447 48.8762 12.0501 50.4777 12.0501H62.1725C63.7741 12.0501 65.0721 10.7447 65.0721 9.13441C65.0721 7.52415 63.7741 6.21875 62.1725 6.21875H50.4777Z"
                      fill="#E27900"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M56.3258 0.386719C57.9361 0.386719 59.2415 1.68489 59.2415 3.28626V14.9811C59.2415 16.5825 57.9361 17.8807 56.3258 17.8807C54.7155 17.8807 53.4102 16.5825 53.4102 14.9811V3.28626C53.4102 1.68489 54.7155 0.386719 56.3258 0.386719Z"
                      fill="#E27900"
                    />
                  </svg>
                }
              />
              <StepCard
                stepNumber={4}
                title={translateStKilian(StKilianEnum['section5.steps.step4.title'])}
                description={translateStKilian(StKilianEnum['section5.steps.step4.description'])}
                icon={
                  <svg
                    width="66"
                    height="67"
                    viewBox="0 0 66 67"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M33.0004 66.2899C14.8963 66.2899 0.199219 51.5928 0.199219 33.4887C0.199219 15.3846 14.8963 0.6875 33.0004 0.6875C51.1045 0.6875 65.8016 15.3846 65.8016 33.4887C65.8016 51.5928 51.1045 66.2899 33.0004 66.2899ZM24.12 6.478C17.0372 8.80688 11.1811 13.8473 7.77849 20.3682H18.9222C19.9171 15.3015 21.4631 10.9346 23.3656 7.68071C23.6105 7.26304 23.862 6.86068 24.12 6.478ZM5.94597 24.7417C5.05378 27.497 4.57271 30.4382 4.57271 33.4887C4.57271 36.5392 5.05378 39.4804 5.94597 42.2357H18.2246C17.8791 39.4563 17.6932 36.5217 17.6932 33.4887C17.6932 30.4557 17.8791 27.5211 18.2246 24.7417H5.94597ZM7.77849 46.6092C11.1811 53.1301 17.0372 58.1705 24.12 60.4994C23.862 60.1167 23.6105 59.7144 23.3656 59.2967C21.4631 56.0428 19.9171 51.6759 18.9222 46.6092H7.77849ZM41.8808 60.4994C48.9637 58.1705 54.8198 53.1301 58.2224 46.6092H47.0787C46.0837 51.6759 44.5377 56.0428 42.6352 59.2967C42.3903 59.7144 42.1389 60.1167 41.8808 60.4994ZM60.0549 42.2357C60.9471 39.4804 61.4281 36.5392 61.4281 33.4887C61.4281 30.4382 60.9471 27.497 60.0549 24.7417H47.7762C48.1218 27.5211 48.3077 30.4557 48.3077 33.4887C48.3077 36.5217 48.1218 39.4563 47.7762 42.2357H60.0549ZM58.2224 20.3682C54.8198 13.8473 48.9637 8.80688 41.8808 6.478C42.1389 6.86068 42.3903 7.26304 42.6352 7.68071C44.5377 10.9346 46.0837 15.3015 47.0787 20.3682H58.2224ZM33.0004 5.06099C30.7437 5.06099 28.8325 6.99408 27.1422 9.88715C25.5458 12.6184 24.2513 16.209 23.37 20.3682H42.6308C41.7496 16.209 40.455 12.6184 38.8587 9.88715C37.1683 6.99408 35.2571 5.06099 33.0004 5.06099ZM33.0004 61.9164C35.2571 61.9164 37.1683 59.9833 38.8587 57.0903C40.455 54.359 41.7496 50.7684 42.6308 46.6092H23.37C24.2513 50.7684 25.5458 54.359 27.1422 57.0903C28.8325 59.9833 30.7437 61.9164 33.0004 61.9164ZM43.3765 42.2357C43.7395 39.4716 43.9342 36.5348 43.9342 33.4887C43.9342 30.4426 43.7395 27.5058 43.3765 24.7417H22.6243C22.2613 27.5058 22.0667 30.4426 22.0667 33.4887C22.0667 36.5348 22.2613 39.4716 22.6243 42.2357H43.3765Z"
                      fill="#E27900"
                    />
                  </svg>
                }
              />
            </div>

            <div className="tw-mt-14">
              <button
                onClick={openSalesFormModal}
                className="tw-cursor-pointer tw-border-none tw-bg-black tw-px-6 tw-py-4 tw-font-bold tw-uppercase tw-text-white tw-no-underline"
              >
                {translateStKilian(StKilianEnum['section5.cta_button'])}
              </button>
            </div>
          </Container>
        </div>

        {/* section 6 */}
        <div className="tw-mt-28 tw-bg-white tw-bg-stKilianBgSection tw-bg-cover tw-bg-no-repeat tw-pb-4 tw-text-text lg:tw-mt-60">
          <Container>
            <div className="tw-relative tw-mt-10 tw-grid tw-w-full tw-grid-cols-12 tw-gap-4 tw-px-4">
              <div className="tw-col-span-12 max-lg:tw-flex max-lg:tw-justify-center lg:tw-col-span-5">
                <Image
                  src={ImagesStKilianCtaImage}
                  alt={translateStKilian(StKilianEnum['alt_texts.cta_image'])}
                  width={472}
                  height={472}
                  className="tw-aspect-square tw-max-h-full tw-max-w-full tw-object-cover lg:tw-absolute lg:-tw-top-[25%]"
                />
              </div>
              <div className="tw-col-span-0 lg:tw-col-span-1"></div>
              <div className="tw-col-span-12 tw-my-12 max-lg:tw-flex max-lg:tw-flex-col max-lg:tw-items-center lg:tw-col-span-6">
                <h2 className="tw-w-full tw-text-5xl tw-font-bold tw-uppercase">
                  {translateStKilian(StKilianEnum['section6.title'])}
                </h2>
                <div className="tw-mt-4 tw-flex tw-items-center tw-gap-2">
                  <div className="tw-mr-2 tw-h-0.5 tw-w-20 tw-bg-stKilian"></div>
                  <h3 className="tw-text-xl tw-font-bold tw-uppercase">
                    {translateStKilian(StKilianEnum['section6.subtitle'])}
                  </h3>
                </div>
                <ul className="tw-mt-6 tw-space-y-4 tw-pl-0 tw-font-bold">
                  <li className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-text-xl tw-text-stKilian">✓</span>
                    <span className="">
                      {translateStKilian(StKilianEnum['section6.benefits.official_partner'])}
                    </span>
                  </li>
                  <li className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-text-xl tw-text-stKilian">✓</span>
                    <span className="">
                      {translateStKilian(
                        StKilianEnum['section6.benefits.european_visibility.title']
                      )}
                      <br />
                      <span className="tw-font-normal">
                        {translateStKilian(
                          StKilianEnum['section6.benefits.european_visibility.description']
                        )}
                      </span>
                    </span>
                  </li>
                  <li className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-text-xl tw-text-stKilian">✓</span>
                    <span className="">
                      {translateStKilian(StKilianEnum['section6.benefits.easy_listing.title'])}
                      <br />
                      <span className="tw-font-normal">
                        {translateStKilian(
                          StKilianEnum['section6.benefits.easy_listing.description']
                        )}
                      </span>
                    </span>
                  </li>
                  <li className="tw-flex tw-items-start tw-gap-3">
                    <span className="tw-text-xl tw-text-stKilian">✓</span>
                    <span className="">
                      {translateStKilian(StKilianEnum['section6.benefits.quick_sale.title'])}
                      <br />
                      <span className="tw-font-normal">
                        {translateStKilian(
                          StKilianEnum['section6.benefits.quick_sale.description']
                        )}
                      </span>
                    </span>
                  </li>
                </ul>
                <button
                  onClick={openSalesFormModal}
                  className="tw-mt-10 tw-block tw-w-fit tw-cursor-pointer tw-border-none tw-bg-stKilian tw-px-6 tw-py-3 tw-font-bold tw-uppercase tw-text-text tw-no-underline tw-transition-colors hover:tw-bg-stKilian max-lg:tw-self-start lg:tw-ml-8"
                >
                  {translateStKilian(StKilianEnum['section6.cta_button'])}
                </button>
              </div>
            </div>
          </Container>
        </div>

        <div className="tw-mt-12 tw-bg-text max-nine:tw-pb-10 lg:tw-mt-32">
          <Container>
            <div className="tw-flex tw-w-full tw-justify-center tw-pt-11">
              <div className="tw-flex tw-flex-col tw-items-center">
                <p>Powered by</p>
                <Image
                  src={ImagesStKilianSpiritoryLogo}
                  alt={'Spiritory Logo'}
                  width={133}
                  height={33}
                  className="tw-h-auto tw-max-w-full"
                />
              </div>

              <div className="tw-mx-4 tw-w-[1px] tw-border tw-border-white/40 nine:tw-mx-10"></div>

              <div className="tw-flex tw-flex-col tw-items-center">
                <p>Offizieller Partner</p>
                <Image
                  src={ImagesStKilianLogoOrange}
                  alt={'St. Kilian Logo'}
                  width={111}
                  height={42}
                  className="tw-h-auto tw-max-w-full"
                />
              </div>
            </div>

            <hr className="tw-m-0 tw-mx-auto tw-mt-12 tw-h-px tw-w-full tw-border-none tw-bg-white/40 tw-opacity-100 max-nine:tw-hidden" />
          </Container>
        </div>
      </div>

      {/* Sales Form Modal */}
      <SalesFormModal isOpen={isSalesFormModalOpen} onClose={closeSalesFormModal} />
    </>
  );
}
